import { publicApiClient, apiClient } from '@/lib/api/client';
import { API_CONFIG } from '@/lib/config/index';
import type { LoginRequest, LoginResponse, User, PublicUserResponse, PublicUserResponseWithJWT } from '@/features/user/types/types';

export const authService = {
  /**
   * Validate the JWT of the currently authenticated public user and returns their profile.
   * Uses PublicAllowedGuestAuthMiddleware - works for both real and guest users.
   */
  validate: async (): Promise<PublicUserResponse | null> => {
    try {
      const response = await publicApiClient.get<{ data: { 'public-user': PublicUserResponse } }>(
        '/api/public/v1/public-user/validate'
      );
      // Extract the nested public-user from the response
      return response.data['public-user'] || null;
    } catch (error) {
      // 401 means not authenticated, return null
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { status?: number } };
        if (axiosError.response?.status === 401) {
          return null;
        }
      }
      // For non-401 errors, throw so AuthProvider can handle them differently
      console.error('Error validating authentication:', error);
      throw error;
    }
  },

  /**
   * Check if user is authenticated by calling the /api/public/v1/public-user/validate endpoint
   * This will automatically include the HttpOnly Authorization cookie
   * @deprecated Use validate() instead
   */
  checkAuth: async (): Promise<User | null> => {
    try {
      const response = await fetch(`${API_CONFIG.baseURL}/api/public/v1/public-user/validate`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const userData = await response.json();
        return userData;
      }

      return null;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return null;
    }
  },

  /**
   * Login user with credentials
   * The server should set the HttpOnly Authorization cookie on successful login
   */
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    const response = await publicApiClient.post<LoginResponse>(
      '/api/public/v1/public-user/login',
      credentials
    );
    return response;
  },

  /**
   * Logout user - clears all cookies (both real and guest user)
   */
  logout: async (): Promise<void> => {
    try {
      await publicApiClient.post('/api/public/v1/public-user/logout');
    } catch (error) {
      console.error('Error during logout:', error);
      // Don't throw - logout should always succeed from UI perspective
    }
  },

  /**
   * Get current user profile using the authenticated API client
   * @deprecated Use validate() instead
   */
  getCurrentUser: async (): Promise<User> => {
    const response = await apiClient.get<User>('/api/public/v1/public-user/validate');
    return response;
  },

  /**
   * Register a guest user
   * Creates a dummy user in backend and returns Guest User and Guest JWT
   * JWT is sent in an httpOnly cookie
   */
  registerGuest: async (): Promise<PublicUserResponseWithJWT> => {
    try {
      const response = await publicApiClient.post<{ data: PublicUserResponseWithJWT }>(
        '/api/public/v1/public-user/guest/register',
        null
      );
      return response.data;
    } catch (error) {
      console.error('Error registering guest user:', error);
      throw new Error('Failed to register guest user');
    }
  },
};
